import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getUserById } from '../services/api';
import {
    Container,
    Header,
    Main,
    Sidebar,
    Content,
    SidebarItem,
    Profile,
    NotificationIcon,
    SettingIcon,
    LogoWrapper,
    SidebarFooter,
    LogoImage,
    LeftWrapper,
    ProfileEmail,
    ProfilePlaceholder,
    ProfileImageHidden,
    NotificationDropdown,
    LogoutButton,
    NotificationItem,
    Divider,
    SidebarToggle,
    Backdrop
} from '../style-comps/Dashboard.styles';
import LogoImg from '../Assets/LogoImg.png';
import DashboardIcon from '../Assets/DashboardIcon.svg';
import ListingIcon from '../Assets/ListingIcon.svg';
import CalendarIcon from '../Assets/CalendarIcon.svg';
import RoleIcon from '../Assets/RoleIcon.svg';
import UserKeyIcon from '../Assets/userkey.svg';
import AttentionIcon from '../Assets/AttentionIcon.svg';
import ReviewIcon from '../Assets/ReviewIcon.svg';
import DocumentsIcon from '../Assets/DocumentsIcon.svg';
import ContactUsIcon from '../Assets/ContactUsIcon.svg';
import { Body3 } from '../style-comps/Common.styles';

const Dashboard = ({ userRole, setIsLoggedIn }) => {
    const [users, setUsers] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const [isNotificationClicked, setNotificationClicked] = useState(false);
    const [greeting, setGreeting] = useState('Good Afternoon');
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [nickName, setNickName] = useState('');
    const [roles, setRoles] = useState([]);
    const [reportReady, setReportReady] = useState(false);
    const [notificationViewed, setNotificationViewed] = useState(false);
    const [reportMonthLabel, setReportMonthLabel] = useState('');
    const [showReportNotification, setShowReportNotification] = useState(false);
    const [reportLoading, setReportLoading] = useState(true);
    const [profileImage, setProfileImage] = useState(() => {
        const stored = localStorage.getItem("profileImage");
        if (!stored) {
            localStorage.setItem("profileImage", "/Assets/mrK03.png");
            return "/Assets/mrK03.png";
        }
        return stored;
    });
    const [isUserLoaded, setIsUserLoaded] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);


    useEffect(() => {
        const requireChange = localStorage.getItem("requirePasswordChange");
        const currentPath = location.pathname;

        if (
            requireChange === "true" &&
            !currentPath.includes("/dashboard/setting")
        ) {
            console.warn("🛑 requirePasswordChange 開啟，導向 Setting 頁面");
            navigate("/dashboard/setting");
        }
    }, [location.pathname]);


    useEffect(() => {
        const syncProfileImage = () => {
            const stored = localStorage.getItem("profileImage");
            setProfileImage(stored || "/Assets/mrK01.png");
        };

        syncProfileImage(); // 首次載入執行一次
        window.addEventListener("storage", syncProfileImage);

        return () => window.removeEventListener("storage", syncProfileImage);
    }, []);

    useEffect(() => {
        if (localStorage.getItem('requirePasswordChange') === 'true') return;   // ⬅️ 新增這一行
        if (!checkTokenExpiration()) return;
        const storedNickName = localStorage.getItem('nickName');
        const storedRoles = JSON.parse(localStorage.getItem('roles')) || [];

        const isSuperAdmin = storedRoles.some(r => r.toLowerCase() === 'superadmin');
        if (isSuperAdmin && location.pathname === '/dashboard') {
            navigate('/dashboard/admin-attention');
        } else if (storedRoles.includes('Admin') && !isSuperAdmin && location.pathname === '/dashboard') {
            navigate('/dashboard/userlisting');
        }

        setNickName(storedNickName || 'User');
        setRoles(storedRoles || []);
    }, [location.pathname, navigate]);


    // Token 過期檢查
    const checkTokenExpiration = () => {
        const expiration = localStorage.getItem("tokenExpiration");
        if (expiration && new Date(expiration) < new Date()) {
            alert("Your session has expired, please login again to continue");
            localStorage.clear();
            navigate("/login");
            return false;
        }
        return true;
    };

    useEffect(() => {
        const now = new Date();
        const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const monthName = prevMonthDate.toLocaleString('en-US', { month: 'long' });
        const key = `reportViewed-${monthName}`;

        const hasViewed = localStorage.getItem(key) === 'true';
        setNotificationViewed(hasViewed);  // 根據 localStorage 設置 notificationViewed
    }, []);




    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');
            if (!token || !userId) return;

            try {
                const cachedUserData = localStorage.getItem("userData");
                let userData;
                if (cachedUserData) {
                    console.log("從 localStorage 載入 userData");
                    userData = JSON.parse(cachedUserData);
                } else {
                    userData = await getUserById(userId, token);
                }
                setUsers([userData]);

                const finalAvatar = userData?.Avatar
                    ? userData.Avatar.startsWith("/Assets/")
                        ? userData.Avatar
                        : `/Assets/${userData.Avatar}`
                    : localStorage.getItem("profileImage") || "/Assets/mrK01.png";

                setProfileImage(finalAvatar); // ✅ 這行原本缺少！
                localStorage.setItem("profileImage", finalAvatar);
                window.dispatchEvent(new Event("storage"));
                setIsUserLoaded(true);
                localStorage.setItem("userData", JSON.stringify(userData));
            } catch (error) {
                console.error('❌ 獲取用戶資料失敗:', error);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!storedRoles.includes("Admin")) return;

        const fetchAdminUser = async () => {
            const token = localStorage.getItem("authToken");
            const userId = localStorage.getItem("userId");
            if (!token || !userId) return;
            try {
                const cachedUserData = localStorage.getItem("userData");
                let userData;
                if (cachedUserData) {
                    userData = JSON.parse(cachedUserData);
                } else {
                    userData = await getUserById(userId, token);
                    localStorage.setItem("userData", JSON.stringify(userData));
                }
                const finalAvatar = userData?.Avatar
                    ? userData.Avatar.startsWith("/Assets/")
                        ? userData.Avatar
                        : `/Assets/${userData.Avatar}`
                    : localStorage.getItem("profileImage") || "/Assets/mrK01.png";
                setUsers([userData]);
                setProfileImage(finalAvatar);
                localStorage.setItem("profileImage", finalAvatar);
                window.dispatchEvent(new Event("storage"));
                setIsUserLoaded(true);
            } catch (err) {
                console.error("無法取得 Admin 使用者資料:", err);
            }
        };
        fetchAdminUser();
    }, []);


    // useEffect(() => {
    //     const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]");

    //     // 🚫 Admin 沒有 user listing → 跳過 API，避免 404
    //     if (storedRoles.includes("Admin")) {
    //         console.log("⛔ Admin detected → skip fetchReportingStatus()");
    //         setIsInitialLoading(false);
    //         setReportLoading(false);
    //         return;
    //     }




    //     const fetchReportingStatus = async () => {
    //         setReportLoading(true);

    //         const token = localStorage.getItem("authToken");
    //         const userId = localStorage.getItem("userId");


    //         if (!token || !userId || userId === "null" || userId === "") {
    //             console.warn("⚠️ 無效 userId 或 token，導回登入");
    //             localStorage.clear();
    //             navigate("/");
    //             return;
    //         }

    //         try {
    //             console.log("📤 正在呼叫 API: getListingsByUserId", userId, token);
    //             const listings = await getListingsByUserId(userId, token);
    //             console.log("✅ listings 回傳成功:", listings);

    //             if (!Array.isArray(listings) || listings.length === 0) {
    //                 console.warn("⚠️ 此 userId 尚未有 listings，或 listings 回傳空");
    //                 return;
    //             }

    //             const listingId = listings[0].Id;
    //             const now = new Date();
    //             const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    //             const startDate = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth(), 1)
    //                 .toISOString().split('T')[0];
    //             const endDate = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth() + 1, 0)
    //                 .toISOString().split('T')[0];
    //             const monthName = prevMonthDate.toLocaleString('en-US', { month: 'long' });

    //             setReportMonthLabel(monthName);
    //             const res = await getBatchReportingData([listingId], startDate, endDate);

    //             // ✅ 新 V2 結構：Success[0].Months[].Reporting
    //             const months = res.Success?.[0]?.Months || [];
    //             const prevMonthKey = prevMonthDate.toISOString().slice(0, 7); // "2026-04"
    //             const monthData = months.find(m => m.MonthKey === prevMonthKey);
    //             const data = monthData?.Reporting || {};
    //             console.log("📊 Reporting 回傳資料:", data);

    //             if (data && typeof data === 'object' && (data.TotalReservations > 0 || data.TotalOwnerRevenue > 0)) {
    //                 setReportReady(true);
    //                 const key = `reportViewed-${monthName}`;
    //                 if (localStorage.getItem(key) !== 'true') {
    //                     setShowReportNotification(true);
    //                 }
    //             }
    //         } catch (error) {
    //             const backendMessage = error?.response?.data?.message || error.message;
    //             console.error("❌ Reporting 資料錯誤:", backendMessage);

    //             if (error?.response?.status === 500) {
    //                 setApiError("Connecting to Hostify");
    //             } else {
    //                 setApiError("Failed to fetch data");
    //             }
    //         }
    //         finally {
    //             setReportLoading(false);
    //             setIsInitialLoading(false);
    //         }
    //     };

    //     fetchReportingStatus();
    // }, []);


    useEffect(() => {
        const currentHour = new Date().getHours();
        if (currentHour < 12) {
            setGreeting('Good Morning');
        } else if (currentHour < 18) {
            setGreeting('Good Afternoon');
        } else {
            setGreeting('Good Evening');
        }
    }, []);


    // 4️⃣ 監聽螢幕寬度變化
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (localStorage.getItem('requirePasswordChange') === 'true') return;   // ⬅️ 新增這一行
        const storedRoles = JSON.parse(localStorage.getItem('roles') || '[]');

        const isSA = storedRoles.some(r => r.toLowerCase() === 'superadmin');
        if (isSA && location.pathname === '/dashboard') {
            navigate('/dashboard/admin-attention');
        } else if (storedRoles.includes('Admin') && !isSA && location.pathname === '/dashboard') {
            navigate('/dashboard/userlisting');
        }
    }, [location.pathname, navigate]);





    const adminMenuItems = [
        { label: 'User Listing', path: '/dashboard/userlisting', icon: ContactUsIcon },
        { label: 'Property Listing', path: '/dashboard/property-listing', icon: ListingIcon },
        { label: 'Roles & Permissions', path: '/dashboard/roles-permissions', icon: UserKeyIcon },
        { label: 'User Activity Logs', path: '/dashboard/user-activity-logs', icon: RoleIcon },
        // { label: 'Admin Attention', path: '/dashboard/admin-attention', icon: AttentionIcon },
    ];
    const superAdminMenuItems = [
        { label: 'User Listing', path: '/dashboard/userlisting', icon: ContactUsIcon },
        { label: 'Property Listing', path: '/dashboard/property-listing', icon: ListingIcon },
        { label: 'Admin Attention', path: '/dashboard/admin-attention', icon: AttentionIcon },
        { label: 'Admin Maintenance', path: '/dashboard/admin-maintenance', icon: RoleIcon },
        { label: 'Roles & Permissions', path: '/dashboard/roles-permissions', icon: UserKeyIcon },
    ];


    const userMenuItems = [
        { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon },
        { label: 'Listing', path: '/dashboard/listing', icon: ListingIcon },
        { label: 'Calendar', path: '/dashboard/calendar', icon: CalendarIcon },
        // { label: 'Review', path: '/dashboard/review', icon: ReviewIcon },
        // { label: 'Documents', path: '/dashboard/documents', icon: DocumentsIcon },
        { label: 'Setting', path: '/dashboard/setting', icon: ContactUsIcon },
        // { label: 'Notification', path: '/dashboard/chat', icon: ContactUsIcon },
    ];

    const menuItems = userRole === 'Admin'
        ? adminMenuItems
        : userRole === 'SuperAdmin'
            ? superAdminMenuItems
            : userMenuItems;

    const isCalendarPage = location.pathname.includes('/dashboard/calendar');

    const handleLogout = () => {
        const preservedImage = localStorage.getItem("profileImage");

        const keysToRemove = [
            "authToken",
            "tokenExpiration",
            "userId",
            "userName",
            "nickName",
            "userRoles",
        ];
        keysToRemove.forEach((k) => localStorage.removeItem(k));

        // 清除所有 cookie
        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        if (preservedImage) {
            localStorage.setItem("profileImage", preservedImage);
        }

        setIsLoggedIn(false);
        navigate('/');
    };


    useEffect(() => {
        console.log("🟡 判斷紅點條件", {
            reportLoading,
            showReportNotification,
            notificationViewed,
            result: !reportLoading && showReportNotification && !notificationViewed
        });
    }, [reportLoading, showReportNotification, notificationViewed]);


    return (
        <Container>
            {/* Header */}
            <Header>
                {/* 左側：漢堡按鈕（手機才顯示） */}
                {isMobile && !isSidebarOpen && (
                    <SidebarToggle
                        onClick={() => setSidebarOpen(true)}
                        style={{ position: 'absolute', left: 16 }}
                    />
                )}

                {/* 中間：Logo */}
                <LogoWrapper>
                    <LogoImage src={LogoImg} alt="logo" />
                </LogoWrapper>

                {/* ✅ 手機版 NotificationIcon 單獨放在 Header，absolute right */}
                {/* {isMobile && (
                    <div style={{ position: 'absolute', right: 16 }}>
                        <NotificationIcon
                            isClicked={!reportLoading && showReportNotification && !notificationViewed}
                            onClick={() => setNotificationClicked(!isNotificationClicked)}
                        />
                        {isNotificationClicked && (
                            <NotificationDropdown style={{ top: '60px', right: '0px' }}>
                                <h3>Notification</h3>

                                {reportReady && (
                                    <NotificationItem
                                        onClick={() => {
                                            const key = `reportViewed-${reportMonthLabel}`;  // 使用當月作為 key
                                            localStorage.setItem(key, 'true');  // 設置該月為已讀
                                            setNotificationViewed(true);  // 更新本地狀態為已讀
                                            setShowReportNotification(false);  // 隱藏紅點
                                            navigate('/dashboard');  // 跳轉到儀表板頁面
                                        }}
                                    >
                                        Your Reporting for {reportMonthLabel} is ready!
                                    </NotificationItem>


                                )}
                            </NotificationDropdown>
                        )}


                    </div>
                )} */}

                {/* 桌機版內容 */}
                <LeftWrapper>
                    {!isMobile && <h2>Dashboard</h2>}
                    {!isMobile && (
                        <div>
                            {/* <NotificationIcon
                                isClicked={!reportLoading && showReportNotification && !notificationViewed}
                                onClick={() => setNotificationClicked(!isNotificationClicked)}
                            />

                            {isNotificationClicked && (
                                <NotificationDropdown style={{ top: '60px', right: '0px' }}>
                                    <h3>Notification</h3>

                                    {reportReady && (
                                        <NotificationItem
                                            onClick={() => {
                                                const key = `reportViewed-${reportMonthLabel}`;  // 使用當月作為 key
                                                localStorage.setItem(key, 'true');  // 設置該月為已讀
                                                setNotificationViewed(true);  // 更新本地狀態為已讀
                                                setShowReportNotification(false);  // 隱藏紅點
                                                navigate('/dashboard');  // 跳轉到儀表板頁面
                                            }}
                                        >
                                            Your Reporting for {reportMonthLabel} is ready!
                                        </NotificationItem>




                                    )}
                                </NotificationDropdown>
                            )} */}


                            <SettingIcon onClick={() => navigate('/dashboard/setting')} />
                            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
                        </div>
                    )}
                </LeftWrapper>
            </Header>





            {/* 主內容區域 */}
            <Main>
                {/* Backdrop，當 Sidebar 打開時顯示 */}
                <Backdrop isOpen={isSidebarOpen} onClick={() => setSidebarOpen(false)} />



                {/* Sidebar */}
                <Sidebar isOpen={isSidebarOpen}>
                    <Profile>
                        {isUserLoaded ? (
                            <>
                                <img
                                    src={profileImage}
                                    alt="user"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/Assets/mrK01.png";
                                    }}
                                />
                                <div>
                                    <h2>{greeting}, <br />{roles.some(r => r.toLowerCase() === 'superadmin') ? 'Super Admin' : roles.includes('Admin') ? 'Admin' : nickName || 'User'}</h2>
                                    <ProfileEmail>
                                        {userRole !== 'admin' && users.length > 0 ? users[0].email : ''}
                                    </ProfileEmail>
                                    <Body3>Check Your Revenue anytime</Body3>
                                    <Divider />
                                </div>
                            </>
                        ) : (
                            // 顯示一個 loading 狀態
                            <>
                                <ProfileImageHidden src="/Assets/mrK01.png" alt="loading" />
                                <ProfilePlaceholder />
                            </>
                        )}
                    </Profile>

                    <div>
                        {menuItems.map((item) => (
                            <SidebarItem
                                key={item.path}
                                active={location.pathname === item.path}
                                onClick={() => setSidebarOpen(false)} /* 點擊後關閉側邊欄 */
                            >
                                {item.icon && <img src={item.icon} alt={`${item.label} 圖示`} />}
                                <Link to={item.path}>{item.label}</Link>
                            </SidebarItem>
                        ))}
                    </div>

                    <SidebarFooter>
                        <SettingIcon onClick={() => navigate('/dashboard/setting')} />
                        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
                    </SidebarFooter>


                </Sidebar>

                {/* Admin 限制訪問的頁面 */}
                {userRole === 'Admin' &&
                    !(
                        location.pathname.startsWith('/dashboard/user-setting') ||
                        location.pathname === '/dashboard/userlisting' ||
                        location.pathname === '/dashboard/property-listing' ||
                        location.pathname === '/dashboard/roles-permissions' ||
                        location.pathname === '/dashboard/admin-attention' ||
                        location.pathname.startsWith('/dashboard/property') ||
                        location.pathname === '/dashboard/user-activity-logs' ||
                        location.pathname === '/dashboard/setting'
                    ) ? (
                    <Content>
                        <h2>Unauthorized Access</h2>
                    </Content>
                ) : userRole === 'SuperAdmin' &&
                    !(
                        location.pathname === '/dashboard/admin-attention' ||
                        location.pathname === '/dashboard/admin-maintenance' ||
                        location.pathname === '/dashboard/roles-permissions' ||
                        location.pathname === '/dashboard/userlisting' ||
                        location.pathname === '/dashboard/property-listing' ||
                        location.pathname.startsWith('/dashboard/property') ||
                        location.pathname.startsWith('/dashboard/user-setting')
                    ) ? (
                    <Content>
                        <h2>Unauthorized Access</h2>
                    </Content>
                ) : (
                    isCalendarPage ? (

                        <Outlet />

                    ) : (
                        <Content>
                            <Outlet />
                        </Content>
                    )
                )}

            </Main>
        </Container>
    );
};

export default Dashboard;